import { useLazyQuery, useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import { and, equals, includes, map, not, or } from 'ramda';
import React, { FC, useContext, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';

import { IAuthenticatedMember, SubscriptionInfo } from '@hooks/useMemberInfo';
import { ICurrentMemberWithSubscriptions } from '@hooks/useSubscriptionInfo';

import { keysToCamel } from '@utils/butter';
import { doesNotExist, exists } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IPageProps } from '@folx-types/flow-steps';

import { membership, membershipName, queryName } from '@graphql/fragments/membership';
import CURRENT_MEMBER_WITH_SUBSCRIPTIONS from '@graphql/queries/currentMemberAllSubscriptions.gql';

import MembershipCheck, { IMembershipCheckPageContent } from './MembershipCheck.component';

interface IMembershipCheckPageProps extends IPageProps {}

const MembershipCheckPage: FC<IMembershipCheckPageProps> = ({ path, ...props }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "membership-check-step" }) {
				page_header
				page_info
				page_fineprint
				help_link
				help_href
			}
		}
	`);

	const [onReturn, setOnReturn] = useState('');
	const [requireOnboarding, setRequireOnboarding] = useState(false);
	const [onComplete, setOnComplete] = useState(false);

	useDeepCompareEffect(() => {
		setOnReturn(props?.location?.state?.onReturn);
		setRequireOnboarding(!!props?.location?.state?.requireOnboarding);
	}, [props?.location?.state]);

	const refinedContent: IMembershipCheckPageContent = keysToCamel(
		pageContent
	) as IMembershipCheckPageContent;

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const [fetchMembership, { data, loading: membershipLoading, error }] = useLazyQuery<
		IAuthenticatedMember<SubscriptionInfo>
	>(combineMemberFragments([membershipName], [membership], queryName), {
		client: graphQLv2Client,
		fetchPolicy: 'network-only',
		onCompleted: () => !requireOnboarding && setOnComplete(true),
	});

	const [
		fetchOnboarding,
		{
			loading: memberSubscriptionsLoading,
			data: memberSubscriptionsData,
			error: memberSubscriptionsError,
		},
	] = useLazyQuery<ICurrentMemberWithSubscriptions>(CURRENT_MEMBER_WITH_SUBSCRIPTIONS, {
		fetchPolicy: 'network-only',
		onCompleted: () => requireOnboarding && setOnComplete(true),
	});

	const loading = membershipLoading || memberSubscriptionsLoading;

	const hasOnboardingError = and(not(loading), exists(memberSubscriptionsError));
	const hasMembershipError = and(
		not(loading),
		or(doesNotExist(data?.authenticatedMember?.membershipSubscription?.type), exists(error))
	);
	const hasError = requireOnboarding
		? hasMembershipError || hasOnboardingError
		: hasMembershipError;

	const membershipActive = equals(
		data?.authenticatedMember?.membershipSubscription?.status,
		'ACTIVE'
	);
	const onboardingComplete = includes(
		'MEMBERSHIP',
		map(
			(n) => n.subscriptionType,
			memberSubscriptionsData?.currentMember.memberSubscriptions?.nodes ?? []
		) ?? []
	);
	const isActive = !loading
		? requireOnboarding
			? onboardingComplete && membershipActive
			: membershipActive
		: false;

	return (
		<MembershipCheck
			pageContent={refinedContent}
			path={path}
			fetchMembership={fetchMembership}
			fetchOnboarding={fetchOnboarding}
			error={hasError}
			active={isActive}
			loading={loading}
			onReturn={onReturn}
			onComplete={onComplete}
			setOnComplete={setOnComplete}
		/>
	);
};

export default MembershipCheckPage;
